import { createSelector } from 'reselect';
import { AppState } from '../..';
import { PlanEmergencyContact, PlanPoint } from './models';

const selectPlanDetails = (state: AppState) => state.planDetails;

const sortEmergencyContacts = (
  a: PlanEmergencyContact,
  b: PlanEmergencyContact,
) => (a.updatedAt > b.updatedAt ? -1 : 1);

export const selectPlan = createSelector(
  selectPlanDetails,
  (planDetails) => planDetails,
);

// Using redux toolkit, create a selector to get the plan points by a given id
export const selectPointById = (
  id: string,
): ((state: AppState) => PlanPoint | undefined) =>
  createSelector(selectPlanDetails, (planDetails) => planDetails.points[id]);

export const selectPlanIsLoggingOn = createSelector(
  selectPlanDetails,
  (details) => details.planIsLoggingOn,
);
export const selectPlanHasDiving = createSelector(
  selectPlanDetails,
  (details) => details.isDiving,
);

export const selectPoints = createSelector(
  selectPlanDetails,
  (state: AppState) => state.floatPlan,
  (planDetails, currentPlan) => {
    const isRoundTrip = currentPlan.time_type !== 'arrival';
    const points =
      Object.values(planDetails.points).sort((a, b) =>
        a.ordinal < b.ordinal ? -1 : 1,
      ) || [];

    const firstPoint = points[0];
    const lastPoint = points[points.length - 1];

    return points.map((point) => {
      let stepDescription = `${point.ordinal + 1}`;
      if (isRoundTrip && point.id === lastPoint.id) {
        stepDescription = `${firstPoint.ordinal + 1}`;
      }

      return {
        ...point,
        stepDescription,
      };
    });
  },
);

export const selectAllEmergencyContacts = createSelector(
  (state: AppState) => state.planDetails.emergencyContacts,
  (state: AppState) => state.planDetails.selectedEmergencyContacts,
  (emergencyContacts, selectedEmergencyContacts) => {
    const allContacts = {
      ...emergencyContacts,
      ...selectedEmergencyContacts,
    };

    return Object.values(allContacts)
      .map((contact) => ({
        ...contact,
        selected: !!selectedEmergencyContacts[contact.id],
      }))
      .sort(sortEmergencyContacts);
  },
);

export const selectSelectedEmergencyContacts = createSelector(
  selectAllEmergencyContacts,
  (emergencyContacts) =>
    emergencyContacts
      .filter((contact) => contact.selected)
      .sort(sortEmergencyContacts),
);
